"use client";

import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect, useSyncExternalStore } from "react";
import { useUserSession } from "./UserSessionProvider";

const PLAIN_APP_ID = "liveChatApp_01JQY91AYX87RNAKQS2694SAZ0";

/**
 * Public routes where the visitor is anonymous by design, so a chat launcher
 * would only produce unattributable threads. The page copy on these routes
 * already points people at email.
 */
const HIDDEN_PATH_PREFIXES = ["/join"];

export interface PlainCustomer {
  email: string;
  /** Server-side HMAC of the email; without it Plain treats the email as unverified. */
  emailHash?: string;
  fullName?: string;
}

interface PlainCustomerDetails {
  email?: string;
  emailHash?: string;
  fullName?: string;
}

interface PlainWidget {
  init(config: {
    appId: string;
    hideLauncher?: boolean;
    customerDetails?: PlainCustomerDetails;
  }): void;
  update(config: { hideLauncher?: boolean }): void;
  setCustomerDetails(details: PlainCustomerDetails): void;
  isInitialized(): boolean;
}

declare global {
  interface Window {
    Plain?: PlainWidget;
  }
}

/**
 * Identity pushed by surfaces that authenticate outside the Stytch session
 * (the creator portal, whose users only ever hold a signed portal token).
 * Kept in a module store rather than props because the widget mounts in the
 * root layout and the identity resolves later, from a tRPC query deep in
 * the tree.
 *
 * One slot, one caller (PortalShell). A second concurrent caller would
 * clobber it and either unmount would clear it — key it per caller before
 * adding one.
 */
let customerOverride: PlainCustomer | null = null;
const listeners = new Set<() => void>();

function setCustomerOverride(customer: PlainCustomer | null) {
  customerOverride = customer;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getCustomerOverride() {
  return customerOverride;
}

function getServerSnapshot(): PlainCustomer | null {
  return null;
}

/**
 * Identify the current visitor to the support widget for as long as the
 * calling component is mounted. Pass null while the identity is unknown.
 */
export function usePlainCustomer(customer: PlainCustomer | null) {
  const email = customer?.email;
  const emailHash = customer?.emailHash;
  const fullName = customer?.fullName;
  useEffect(() => {
    if (!email) return;
    setCustomerOverride({ email, emailHash, fullName });
    return () => setCustomerOverride(null);
  }, [email, emailHash, fullName]);
}

function isHiddenPath(pathname: string | null) {
  if (!pathname) return false;
  return HIDDEN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * The widget once it is safe to talk to. Guarded by shape, not just presence:
 * this runs on every route change app-wide, so a method missing from a future
 * script version must degrade to "do nothing", not throw on every navigation.
 */
function getPlain(): PlainWidget | null {
  const plain = window.Plain;
  if (
    !plain ||
    typeof plain.isInitialized !== "function" ||
    typeof plain.update !== "function" ||
    typeof plain.setCustomerDetails !== "function"
  ) {
    return null;
  }
  return plain.isInitialized() ? plain : null;
}

export function PlainComponent() {
  const session = useUserSession();
  const pathname = usePathname();
  const override = useSyncExternalStore(
    subscribe,
    getCustomerOverride,
    getServerSnapshot,
  );
  const hideLauncher = isHiddenPath(pathname);

  const sessionName = session?.member.name;
  const customer: PlainCustomerDetails | undefined = override
    ? override
    : session
      ? {
          email: session.member.email_address,
          emailHash: session.plainHash,
          fullName:
            sessionName && sessionName.length > 0 ? sessionName : undefined,
        }
      : undefined;
  const customerEmail = customer?.email;
  const customerEmailHash = customer?.emailHash;
  const customerFullName = customer?.fullName;

  useEffect(() => {
    getPlain()?.update({ hideLauncher });
  }, [hideLauncher]);

  // Pushed in both directions: logout is a same-tab navigation, so this
  // component outlives the session and the widget would otherwise keep
  // attributing chats to the person who just signed out.
  useEffect(() => {
    getPlain()?.setCustomerDetails(
      customerEmail
        ? {
            email: customerEmail,
            emailHash: customerEmailHash,
            fullName: customerFullName,
          }
        : {},
    );
  }, [customerEmail, customerEmailHash, customerFullName]);

  return (
    <Script
      src="https://chat.cdn-plain.com/index.js"
      onLoad={() => {
        window.Plain?.init({
          appId: PLAIN_APP_ID,
          hideLauncher,
          customerDetails: customer,
        });
      }}
    />
  );
}
